/* global.js's trapFocus()/removeTrapFocus() call elementToFocus.focus() without
   { preventScroll: true }. Since one of the focus targets they can land on is
   .drawer__inner (or an element inside it), that focus() call makes the browser
   scroll the drawer's own scrollable area back into view — i.e. back to the top —
   every time focus gets trapped/restored (cart add, quantity change, discount
   apply/remove, etc). Patch both here, scoped narrowly: only restore the cart
   drawer's own scroll position, don't touch other drawers/modals on the page. */
(function () {
  if (window.__cartDrawerTrapFocusPatched) return;
  window.__cartDrawerTrapFocusPatched = true;
  if (typeof window.trapFocus !== 'function' || typeof window.removeTrapFocus !== 'function') return;

  const originalTrapFocus = window.trapFocus;
  const originalRemoveTrapFocus = window.removeTrapFocus;

  function getDrawerScrollTop() {
    const drawerInner = document.querySelector('cart-drawer .drawer__inner');
    return drawerInner ? drawerInner.scrollTop : null;
  }
  function restoreDrawerScrollTop(scrollTop) {
    if (scrollTop === null) return;
    const drawerInner = document.querySelector('cart-drawer .drawer__inner');
    if (drawerInner) drawerInner.scrollTop = scrollTop;
  }

  window.trapFocus = function (container, elementToFocus) {
    const savedScrollTop = getDrawerScrollTop();
    originalTrapFocus(container, elementToFocus);
    restoreDrawerScrollTop(savedScrollTop);
  };

  window.removeTrapFocus = function (elementToFocus) {
    const savedScrollTop = getDrawerScrollTop();
    originalRemoveTrapFocus(elementToFocus);
    restoreDrawerScrollTop(savedScrollTop);
  };
})();

/* Cart drawer quantity +/- buttons: intercept in the capture phase, before the
   shared <quantity-input> component's own bubble-phase handler runs, and fully
   take over for this click (stopImmediatePropagation). Reasons:
   1) The shared component updates the input's value via native stepUp()/
      stepDown(), then dispatches a synthetic 'change' event that only reaches
      CartItems after a debounce delay — the value shown in between doesn't
      reliably match what the server confirms, which read as the number
      flickering to a wrong value while loading.
   2) Decrementing from 1 relies on that same indirect chain reaching the
      server with quantity 0; the trash button already removes a line item
      reliably by calling cartItems.updateQuantity(index, 0) directly, so we
      use that exact same proven call here instead of going through the
      native stepper at all. */
document.addEventListener(
  'click',
  function (event) {
    const btn = event.target.closest('.cart-drawer__qty-btn');
    if (!btn) return;
    const wrapper = btn.closest('quantity-input');
    const input = wrapper && wrapper.querySelector('.cart-drawer__qty-input');
    if (!input) return;
    const cartItems = wrapper.closest('cart-drawer-items') || wrapper.closest('cart-items');
    if (!cartItems || typeof cartItems.updateQuantity !== 'function') return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const current = parseInt(input.value, 10) || 0;
    const max = parseInt(input.max, 10);
    let next = btn.name === 'plus' ? current + 1 : current - 1;
    if (next < 0) next = 0;
    if (!Number.isNaN(max) && next > max) next = max;
    if (next === current) return;

    input.value = next;
    cartItems.updateQuantity(input.dataset.index, next, btn.name, input.dataset.quantityVariantId);
  },
  true
);

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    document.querySelectorAll('[id*="cart-icon-bubble"]').forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.classList.remove('just-added');
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.classList.remove('just-added');
          this.open(cartLink);
        }
      });
    })
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('active');
      this.classList.add('animate');
      document.body.classList.add('cart-drawer-open');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    if(document.querySelectorAll('.popup-modal.active').length == 0){
      document.body.style.paddingRight = this.getScrollbarWidth() + 'px';
      document.body.classList.add('overflow-hidden');
    }
  }

  close() {
    document.body.classList.remove('cart-drawer-open');
    this.classList.remove('animate');
    this.classList.remove('just-added');
    setTimeout(() => {
      this.classList.remove('active');
      if(document.querySelectorAll('.popup-modal.active').length == 0){
        document.body.classList.remove('overflow-hidden');
        document.body.style.paddingRight = '';
      }
    }, 500);
    removeTrapFocus(this.activeElement);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const previousInner = this.querySelector('.drawer__inner');
    const savedScrollTop = previousInner ? previousInner.scrollTop : 0;
    const wasActive = this.classList.contains('active');

    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.classList.add('just-added');
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if(sectionElement) sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if(section.id == 'cart-free-delivery' && document.querySelector('#product-page-free-delivery')){
        document.querySelector('#product-page-free-delivery').innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
      if(section.id == 'cart-icon-bubble' && document.querySelector('.footer-sticky-nav__item__icon .cart-count-bubble')){
        const parser = new DOMParser();
        const bubble = parser.parseFromString(this.getSectionInnerHTML(parsedState.sections[section.id], section.selector), 'text/html');
        var ib = bubble.querySelector('.cart-count-bubble')?bubble.querySelector('.cart-count-bubble').innerHTML : '' ;
        document.querySelector('.footer-sticky-nav__item__icon .cart-count-bubble').innerHTML = ib;
      }
    });

    const newInner = this.querySelector('.drawer__inner');
    if (newInner) newInner.scrollTop = savedScrollTop;

    if (!wasActive) {
      setTimeout(() => {
        this.open();
      });
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
      {
        id: 'cart-icon-bubble--mobile'
      },
      {
        id: 'cart-free-delivery'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
  getScrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-icon-bubble--mobile',
        section: 'cart-icon-bubble--mobile',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

if (!customElements.get('cart-drawer-fbt')) {
  customElements.define(
    'cart-drawer-fbt',
    class CartDrawerFbt extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', (event) => {
          const btn = event.target.closest('[data-add-variant]');
          if (!btn || btn.classList.contains('is-loading')) return;
          event.preventDefault();
          this.addItem(btn);
        });
      }

      addItem(btn) {
        btn.classList.add('is-loading');
        const errorEl = this.parentElement.querySelector('.cart-drawer__fbt-error');
        if (errorEl) errorEl.textContent = '';

        const body = JSON.stringify({
          items: [{ id: btn.dataset.addVariant, quantity: 1 }],
          sections: ['cart-drawer', 'cart-icon-bubble', 'cart-icon-bubble--mobile'],
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.status) {
              if (errorEl) errorEl.textContent = parsedState.description || parsedState.message;
              btn.classList.remove('is-loading');
              return;
            }

            const cartDrawerInner = document.getElementById('CartDrawer');
            if (cartDrawerInner && parsedState.sections['cart-drawer']) {
              const newInner = new DOMParser()
                .parseFromString(parsedState.sections['cart-drawer'], 'text/html')
                .getElementById('CartDrawer');
              if (newInner) cartDrawerInner.innerHTML = newInner.innerHTML;
            }

            ['cart-icon-bubble', 'cart-icon-bubble--mobile'].forEach((id) => {
              const target = document.getElementById(id);
              const html = parsedState.sections[id];
              if (target && html) {
                const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
                if (source) target.innerHTML = source.innerHTML;
              }
            });

            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) cartDrawer.classList.remove('is-empty');

            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-fbt', cartData: parsedState });
          })
          .catch(() => {
            if (errorEl) errorEl.textContent = window.cartStrings.error;
            btn.classList.remove('is-loading');
          });
      }
    }
  );
}

// Pack upgrade row: swap the item's variant for the bigger pack in one request, then re-render the drawer
if (!customElements.get('cart-drawer-upgrade')) {
  customElements.define(
    'cart-drawer-upgrade',
    class CartDrawerUpgrade extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', (event) => {
          const btn = event.target.closest('[data-upgrade-to]');
          if (!btn || btn.classList.contains('is-loading')) return;
          event.preventDefault();
          this.upgrade(btn);
        });
      }

      upgrade(btn) {
        btn.classList.add('is-loading');
        const errorEl = this.querySelector('.cart-drawer__upgrade-error');
        if (errorEl) errorEl.textContent = '';

        const body = JSON.stringify({
          updates: { [btn.dataset.upgradeFrom]: 0, [btn.dataset.upgradeTo]: 1 },
          sections: ['cart-drawer', 'cart-icon-bubble', 'cart-icon-bubble--mobile'],
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.status || !parsedState.sections) {
              if (errorEl) errorEl.textContent = parsedState.description || parsedState.message || window.cartStrings.error;
              btn.classList.remove('is-loading');
              return;
            }

            const cartDrawerInner = document.getElementById('CartDrawer');
            if (cartDrawerInner && parsedState.sections['cart-drawer']) {
              const newInner = new DOMParser()
                .parseFromString(parsedState.sections['cart-drawer'], 'text/html')
                .getElementById('CartDrawer');
              if (newInner) cartDrawerInner.innerHTML = newInner.innerHTML;
            }

            ['cart-icon-bubble', 'cart-icon-bubble--mobile'].forEach((id) => {
              const target = document.getElementById(id);
              const html = parsedState.sections[id];
              if (target && html) {
                const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
                if (source) target.innerHTML = source.innerHTML;
              }
            });

            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-upgrade', cartData: parsedState });
          })
          .catch(() => {
            if (errorEl) errorEl.textContent = window.cartStrings.error;
            btn.classList.remove('is-loading');
          });
      }
    }
  );
}
